"use client";

import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { toast } from "@shared/components/ui/snackbar";
import { Switch } from "@shared/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/components/ui/tooltip";
import { formatLocalDateTime } from "@shared/lib/formatters";
import { IconInfoCircle } from "@tabler/icons-react";
import { gql } from "graphql-request";
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AdminUsersOverviewTab } from "@/components/admin.users.overview-tab";
import { type AdminCreditLedgerEntry, useAdminGrantOrgCredit, useAdminOrgBalance } from "@/lib/admin/credits.hooks";
import {
  type AdminOrgRow,
  type AdminProjectRow,
  useAdminAllOrgs,
  useAdminAllowlistAdd,
  useAdminAllowlistRemove,
  useAdminAllUsers,
} from "@/lib/admin/directory.hooks";
import { adminPath } from "@/lib/auth";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";

// ─── HubSpot brand icon (inline SVG — not in Tabler Icons) ────────────────────

const HubSpotIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M18.164 7.93V5.82a1.56 1.56 0 0 0 .898-1.408V4.36a1.56 1.56 0 0 0-1.56-1.56h-.043a1.56 1.56 0 0 0-1.56 1.56v.054a1.56 1.56 0 0 0 .898 1.406v2.12a4.42 4.42 0 0 0-2.1.924L8.76 4.745a1.745 1.745 0 1 0-1.03.894L14.1 9.826a4.43 4.43 0 0 0-.617 2.27 4.43 4.43 0 0 0 .614 2.267l-1.895 1.384a1.4 1.4 0 1 0 .898 1.188 1.4 1.4 0 0 0-.082-.472l1.918-1.4a4.46 4.46 0 0 0 2.735.934 4.46 4.46 0 0 0 4.46-4.46 4.46 4.46 0 0 0-3.966-4.607zm-.708 7.184a2.577 2.577 0 1 1 2.577-2.577 2.577 2.577 0 0 1-2.577 2.577z" />
  </svg>
);

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminEntityDialogProps {
  entityType: "user" | "project" | "org";
  entityId: string;
  open: boolean;
  onClose: () => void;
  initialTab?: string;
}

// ─── Credits tab ─────────────────────────────────────────────────────────────

const microToUsd = (microStr: string) =>
  (parseInt(microStr, 10) / 1_000_000).toLocaleString("en-US", { style: "currency", currency: "USD" });

const CreditsTab = ({
  orgId,
  orgName,
  amountRef,
  focusOnLoad,
  projectSpendLimitMicro,
}: {
  orgId: string;
  orgName?: string | null;
  amountRef?: RefObject<HTMLInputElement | null>;
  focusOnLoad?: boolean;
  /** When opened from a project, surface that project's spend limit (credits remain org-scoped). */
  projectSpendLimitMicro?: string | null;
}) => {
  const { data, isLoading, isError, refetch } = useAdminOrgBalance(orgId);
  const detail = data?.adminOrgBalance;

  const internalRef = useRef<HTMLInputElement>(null);
  const resolvedRef = amountRef ?? internalRef;

  // Focus the amount input once data loads and arm the dialog-level focus trap.
  // The trap (onFocusCapture on DialogContent) redirects any other element that
  // grabs focus back to the amount input for 1500ms.
  // Stable-ref deps intentionally omitted — effect must NOT re-run on refetches.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resolvedRef is a stable ref — intentionally omitted.
  useEffect(() => {
    if (!focusOnLoad) return;

    const tryFocus = () => {
      const input = resolvedRef.current;
      if (!input) return false;
      input.focus();
      return true;
    };

    if (!tryFocus()) {
      const timer = setInterval(() => {
        if (tryFocus()) clearInterval(timer);
      }, 50);
      return () => clearInterval(timer);
    }
  }, [focusOnLoad]);

  const [amountDollars, setAmountDollars] = useState("");
  const [reason, setReason] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const grant = useAdminGrantOrgCredit(() => {
    toast.success("Organization balance updated.");
    setAmountDollars("");
    setReason("");
    setIdempotencyKey(crypto.randomUUID());
    void refetch();
  });

  const parsedAmount = parseFloat(amountDollars);
  const isClawback = Number.isFinite(parsedAmount) && parsedAmount < 0;

  const handleGrant = (e: React.FormEvent) => {
    e.preventDefault();
    const dollars = parseFloat(amountDollars);
    if (!Number.isFinite(dollars) || dollars === 0) {
      toast.error("Enter a non-zero dollar amount.");
      return;
    }
    grant.mutate({
      org_id: orgId,
      amount_cents: Math.round(dollars * 100),
      reason,
      idempotency_key: idempotencyKey,
    });
  };

  const resolvedOrgName = orgName ?? detail?.name ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded border border-primary/20 bg-muted/30 px-3 py-2 text-sm">
        <p className="font-medium text-foreground">Organization credits</p>
        <p className="text-xs text-muted-foreground">
          Credits and payment methods belong to the organization wallet
          {resolvedOrgName ? (
            <>
              {" "}
              (<span className="font-medium text-foreground">{resolvedOrgName}</span>)
            </>
          ) : null}
          , not to individual projects. Usage from any project in this org draws from this balance.
        </p>
      </div>

      {projectSpendLimitMicro !== undefined && (
        <div className="rounded border px-3 py-2 text-sm">
          <p className="font-medium text-foreground">Project monthly spend limit</p>
          <p className="text-xs text-muted-foreground">
            This project&apos;s spend limit is{" "}
            <span className="font-medium tabular-nums text-foreground">
              {projectSpendLimitMicro == null ? "not set" : microToUsd(projectSpendLimitMicro)}
            </span>
            . Spend limits are project-scoped; they do not change the organization credit balance.
          </p>
        </div>
      )}

      {/* Balance summary — shown only once data is loaded */}
      {isLoading && <p className="py-2 text-sm text-muted-foreground">Loading organization balance…</p>}
      {isError && <p className="py-2 text-sm text-destructive">Failed to load balance.</p>}
      {detail && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {(
              [
                ["Organization balance", detail.credit_balance_micro, true],
                ["Granted remaining", detail.granted_balance_micro, false],
                ["Purchased remaining", detail.purchased_balance_micro, false],
              ] as const
            ).map(([label, value, primary]) => (
              <div
                key={label}
                className={`flex flex-col gap-1 rounded border p-3 ${primary ? "border-primary/40 bg-muted/40" : ""}`}
              >
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-lg font-semibold tabular-nums">{microToUsd(value)}</p>
              </div>
            ))}
          </div>

          {/* Recent transactions */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">
              Recent organization transactions <span className="text-xs text-muted-foreground">(last 50)</span>
            </p>
            <div className="max-h-52 overflow-auto rounded border">
              <Table className="min-w-max">
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance after</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead className="text-right">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.recent_transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-4 text-center text-sm text-muted-foreground">
                        No transactions.
                      </TableCell>
                    </TableRow>
                  )}
                  {detail.recent_transactions.map((tx: AdminCreditLedgerEntry) => {
                    const isNeg = parseInt(tx.amount_micro, 10) < 0;
                    const typeLabel =
                      tx.type === "adjustment"
                        ? isNeg
                          ? "Claw back"
                          : "Grant"
                        : tx.type === "topup"
                          ? "Top-up"
                          : tx.type;
                    return (
                      <TableRow key={tx.id}>
                        <TableCell className="text-table">{typeLabel}</TableCell>
                        <TableCell
                          className={`text-right tabular-nums text-table ${isNeg ? "text-destructive" : "text-green-600"}`}
                        >
                          {microToUsd(tx.amount_micro)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-table text-muted-foreground">
                          {microToUsd(tx.balance_after_micro)}
                        </TableCell>
                        <TableCell className="max-w-36 truncate text-table text-muted-foreground">
                          {tx.reason ?? tx.source ?? "—"}
                        </TableCell>
                        <TableCell className="max-w-32 truncate text-table text-muted-foreground">
                          {tx.granted_by_email ?? "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right text-table text-muted-foreground">
                          {formatLocalDateTime(tx.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}

      {/* Grant form — always rendered so amountRef is in the DOM immediately */}
      <form onSubmit={handleGrant} className="flex flex-col gap-3 rounded border bg-muted/20 p-4">
        <div>
          <p className="text-sm font-medium">Grant / claw back organization credits</p>
          <p className="text-xs text-muted-foreground">
            Positive to grant, negative to claw back. Applied to the organization wallet, not this project alone.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Amount (USD)</Label>
            <Input
              ref={resolvedRef}
              type="number"
              step="0.01"
              placeholder="e.g. 10.00"
              value={amountDollars}
              onChange={(e) => {
                setAmountDollars(e.target.value);
                setIdempotencyKey(crypto.randomUUID());
              }}
              className="w-36"
              required
            />
          </div>
          <div className="flex min-w-48 flex-1 flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Reason</Label>
            <Input
              type="text"
              placeholder="e.g. Promo — beta tester"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setIdempotencyKey(crypto.randomUUID());
              }}
              required
              maxLength={255}
            />
          </div>
          <Button type="submit" disabled={grant.isPending}>
            {grant.isPending ? "Saving…" : isClawback ? "Claw back" : "Apply"}
          </Button>
        </div>
      </form>

      {/* Stripe link */}
      {detail?.stripe_dashboard_url && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Organization payment customer:</span>
          {detail.stripe_customer_id && (
            <Input
              readOnly
              value={detail.stripe_customer_id}
              onFocus={(e) => e.currentTarget.select()}
              className="h-7 w-48 cursor-text font-mono text-xs"
            />
          )}
          <a
            href={detail.stripe_dashboard_url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            View in portal
          </a>
        </div>
      )}
    </div>
  );
};

// ─── Org projects tab (capacity + spend limit) ────────────────────────────────

const OrgProjectsTab = ({
  org,
  onProjectsChanged,
}: {
  org: AdminOrgRow | null | undefined;
  onProjectsChanged: () => void;
}) => {
  const mAdd = useAdminAllowlistAdd();
  const mRemove = useAdminAllowlistRemove();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  if (!org) {
    return <p className="py-4 text-sm text-muted-foreground">Loading projects…</p>;
  }

  if (org.projects.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">No projects in this organization.</p>;
  }

  const handleToggle = (row: AdminProjectRow) => {
    setTogglingId(row.id);
    const op = row.allowlisted ? mRemove.mutateAsync({ project_id: row.id }) : mAdd.mutateAsync({ project_id: row.id });
    void op
      .then(() => {
        toast.success(row.allowlisted ? "Removed from allowlist" : "Added to allowlist");
        onProjectsChanged();
      })
      .catch(() => toast.error("Failed to update allowlist"))
      .finally(() => setTogglingId(null));
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Capacity allowlist and monthly spend limits are project-scoped. Credits remain on the organization wallet.
      </p>
      <div className="rounded border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Spend limit</TableHead>
              <TableHead>Capacity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {org.projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell className="text-table font-medium">{project.name || "Untitled project"}</TableCell>
                <TableCell className="text-table text-muted-foreground tabular-nums">
                  {project.monthly_spend_limit_micro == null
                    ? "Not set"
                    : microToUsd(project.monthly_spend_limit_micro)}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={project.allowlisted}
                    disabled={togglingId === project.id}
                    onCheckedChange={() => handleToggle(project)}
                    aria-label={`Capacity for ${project.name || project.id}`}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ─── Org users tab ────────────────────────────────────────────────────────────

const OrgUsersTab = ({ orgId }: { orgId: string }) => {
  const enabled = true;
  const usersQuery = useAdminAllUsers(false, enabled);

  const users = (usersQuery.data?.adminAllUsers ?? []).filter((u) => u.org_id === orgId);

  if (usersQuery.isPending) {
    return <p className="py-4 text-sm text-muted-foreground">Loading users…</p>;
  }

  if (users.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">No users found in this organization.</p>;
  }

  return (
    <div className="rounded border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="text-table font-medium">{user.name}</TableCell>
              <TableCell>
                <a href={adminPath(`/users/${user.id}`)} className="text-table underline-offset-4 hover:underline">
                  {user.email}
                </a>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// ─── HubSpot types ────────────────────────────────────────────────────────────

interface HubspotAdminUser {
  user_id: string;
  email: string;
  local_properties: Record<string, string | null> | null;
  hubspot_properties: Record<string, string | null> | null;
  drift: Array<{ property: string; local_value: string | null; hubspot_value: string | null }>;
  hubspot_contact_id: string | null;
  hubspot_portal_id: string | null;
}

const HUBSPOT_USER_QUERY = gql`
  query AdminHubspotUser($user_id: ID!) {
    adminHubspotUser(user_id: $user_id) {
      user_id
      email
      local_properties
      hubspot_properties
      drift { property local_value hubspot_value }
      hubspot_contact_id
      hubspot_portal_id
    }
  }
`;

// ─── Properties tab (user only) ───────────────────────────────────────────────

const SYNC_INFO = `HubSpot contact properties are read from HubSpot for comparison.
Updates into HubSpot are pushed by Fivetran.
See services/frontend/api/docs/hubspot-integration.md.`;

const PropertiesTab = ({ userId }: { userId: string }) => {
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useGraphqlQuery<{ adminHubspotUser: HubspotAdminUser }, { user_id: string }>({
    queryKey: ["adminHubspotUser", userId],
    document: HUBSPOT_USER_QUERY,
    variables: { user_id: userId },
    enabled: Boolean(userId),
  });

  const hs = data?.adminHubspotUser;

  const hsUrl =
    hs?.hubspot_portal_id && hs?.hubspot_contact_id
      ? `https://app.hubspot.com/contacts/${hs.hubspot_portal_id}/record/0-1/${hs.hubspot_contact_id}`
      : null;

  const localProps = hs?.local_properties ?? {};
  const hubspotProps = hs?.hubspot_properties ?? {};
  const driftSet = new Set((hs?.drift ?? []).map((d) => d.property));
  const allKeys = Array.from(new Set([...Object.keys(localProps), ...Object.keys(hubspotProps)])).sort();
  const contactFound = hs?.hubspot_properties !== null && hs?.hubspot_properties !== undefined;

  const filteredKeys = search.trim()
    ? allKeys.filter((key) => {
        const q = search.toLowerCase();
        return (
          key.toLowerCase().includes(q) ||
          (localProps[key] ?? "").toLowerCase().includes(q) ||
          (hubspotProps[key] ?? "").toLowerCase().includes(q)
        );
      })
    : allKeys;

  if (isLoading) return <p className="py-4 text-sm text-muted-foreground">Loading…</p>;
  if (isError || !hs) return <p className="py-4 text-sm text-destructive">Failed to load properties.</p>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <Input
          placeholder="Filter properties…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 max-w-64 text-sm"
        />
        <div className="flex shrink-0 items-center gap-3">
          <p className="text-sm text-muted-foreground">{contactFound ? "Found in HubSpot" : "Not in HubSpot"}</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-default items-center">
                <IconInfoCircle className="h-4 w-4 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent sideOffset={6} className="max-w-[242px]">
              {SYNC_INFO}
            </TooltipContent>
          </Tooltip>
          {hsUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={hsUrl} target="_blank" rel="noopener noreferrer">
                <HubSpotIcon className="mr-1.5 h-3.5 w-3.5 text-[#FF7A59]" />
                Open in HubSpot
              </a>
            </Button>
          )}
        </div>
      </div>

      {filteredKeys.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {allKeys.length === 0 ? "No properties available." : "No properties match your filter."}
        </p>
      ) : (
        <div className="max-h-96 overflow-auto rounded border">
          <Table className="min-w-max">
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Local value</TableHead>
                <TableHead>HubSpot value</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredKeys.map((key) => {
                const local = localProps[key];
                const remote = hubspotProps[key];
                const isDrift = driftSet.has(key);

                let statusEl: React.ReactNode;
                if (!contactFound) {
                  statusEl = <Badge variant="secondary">Not synced</Badge>;
                } else if (isDrift) {
                  statusEl = <Badge variant="destructive">Out of sync</Badge>;
                } else if (local !== null && local !== undefined) {
                  statusEl = <Badge variant="default">In sync</Badge>;
                } else {
                  statusEl = <span className="text-sm text-muted-foreground">—</span>;
                }

                return (
                  <TableRow key={key}>
                    <TableCell className="font-mono text-table">{key}</TableCell>
                    <TableCell className="text-table">
                      {local != null ? local : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-table">
                      {remote != null ? remote : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>{statusEl}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ─── AdminEntityDialog ────────────────────────────────────────────────────────

export const AdminEntityDialog = ({ entityType, entityId, open, onClose, initialTab }: AdminEntityDialogProps) => {
  const enabled = true;

  const [currentType, setCurrentType] = useState<"user" | "project" | "org">(entityType);
  const [currentId, setCurrentId] = useState<string>(entityId);

  // Keep internal state in sync with props whenever the dialog is open.
  // This covers both the initial open AND switching entities without closing first.
  if (open && (currentId !== entityId || currentType !== entityType)) {
    setCurrentType(entityType);
    setCurrentId(entityId);
  }

  // Fetch HubSpot portal ID so we can build search links (user view only)
  const hubspotHealthQuery = useGraphqlQuery<{ adminHubspotHealth: { portal_id: string | null } }>({
    queryKey: ["adminHubspotHealth"],
    document: gql`query AdminHubspotHealth { adminHubspotHealth { portal_id } }`,
    enabled: enabled && currentType === "user",
  });
  const hsPortalId = hubspotHealthQuery.data?.adminHubspotHealth.portal_id ?? null;

  // Fetch user data via the shared hook so this component reads from the same
  // React Query cache entry as the Users directory. An inline query with fewer
  // fields (e.g. missing created_at) would overwrite that cache entry and strip
  // data like the "Joined" column whenever the dialog is open.
  const usersQuery = useAdminAllUsers(true, enabled && currentType === "user");
  const orgsQuery = useAdminAllOrgs(enabled && (currentType === "org" || currentType === "project"));

  const userRow = usersQuery.data?.adminAllUsers.find((u) => u.id === currentId);

  // Prefer org rows; if a legacy project id was passed, resolve to its parent org.
  const orgRow =
    currentType === "org"
      ? orgsQuery.data?.adminAllOrgs.find((o) => o.id === currentId)
      : currentType === "project"
        ? orgsQuery.data?.adminAllOrgs.find((o) => o.projects.some((p) => p.id === currentId))
        : undefined;

  const resolvedOrgId = currentType === "org" ? currentId : currentType === "project" ? orgRow?.id : userRow?.org_id;
  const resolvedOrgName = currentType === "user" ? (userRow?.org_name ?? null) : (orgRow?.name ?? null);

  const title =
    currentType === "user" ? (userRow?.name ?? "User") : (orgRow?.name ?? (currentType === "org" ? "Org" : "Project"));

  const subtitle = currentType === "user" ? userRow?.email : (orgRow?.slug ?? undefined);

  const userProjects = userRow?.projects ?? [];

  const orgTabs = ["details", "projects", "users"];
  const userTabs = ["overview", "properties"];

  // Ref passed to CreditsTab's amount input.
  const amountInputRef = useRef<HTMLInputElement>(null);
  // Armed by CreditsTab when it focuses the amount input; guards against focus theft.
  const focusTrapActiveUntilRef = useRef<number>(0);

  // Arm the focus trap BEFORE Base UI's dialog focus management runs (useLayoutEffect
  // fires after DOM commit but before the browser paints — same phase as Base UI's
  // own layout effects, and our parent runs before Base UI's children).
  useLayoutEffect(() => {
    if (open && initialTab === "details") {
      focusTrapActiveUntilRef.current = Date.now() + 3000;
    }
    // No else-reset — let the 3000ms window expire naturally.
  }, [open, initialTab]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl md:max-w-3xl lg:max-w-4xl"
        onFocusCapture={(e) => {
          if (Date.now() > focusTrapActiveUntilRef.current) return;
          if (e.target === amountInputRef.current) return;
          // Something else in the dialog grabbed focus during the guard window — steal it back.
          e.preventDefault();
          e.stopPropagation();
          amountInputRef.current?.focus();
        }}
      >
        <DialogHeader className="pr-8">
          <DialogTitle>
            {title}
            {subtitle && <span className="ml-2 text-base font-normal text-muted-foreground">{subtitle}</span>}
          </DialogTitle>
        </DialogHeader>

        {currentType === "user" ? (
          /* ── User view: Overview + Properties tabs ── */
          <Tabs
            key={currentId}
            defaultValue={initialTab && userTabs.includes(initialTab) ? initialTab : "overview"}
            className="w-full"
          >
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="properties">Properties</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <AdminUsersOverviewTab
                key={currentId}
                userId={currentId}
                onDeleted={onClose}
                hsPortalId={hsPortalId}
                projects={userProjects}
              />
            </TabsContent>

            <TabsContent value="properties" className="mt-4">
              <PropertiesTab userId={currentId} />
            </TabsContent>
          </Tabs>
        ) : (
          /* ── Org view (project entityType resolves to parent org) ── */
          <Tabs
            key={resolvedOrgId ?? currentId}
            defaultValue={initialTab && orgTabs.includes(initialTab) ? initialTab : orgTabs[0]}
            className="w-full"
          >
            <TabsList>
              <TabsTrigger value="details">Org credits</TabsTrigger>
              <TabsTrigger value="projects">Projects</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4">
              {resolvedOrgId ? (
                <CreditsTab
                  orgId={resolvedOrgId}
                  orgName={resolvedOrgName}
                  amountRef={amountInputRef}
                  focusOnLoad={initialTab === "details"}
                />
              ) : (
                <p className="py-4 text-sm text-muted-foreground">Loading organization…</p>
              )}
            </TabsContent>

            <TabsContent value="projects" className="mt-4">
              <OrgProjectsTab org={orgRow} onProjectsChanged={() => void orgsQuery.refetch()} />
            </TabsContent>

            <TabsContent value="users" className="mt-4">
              {resolvedOrgId ? (
                <OrgUsersTab orgId={resolvedOrgId} />
              ) : (
                <p className="py-4 text-sm text-muted-foreground">Loading users…</p>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
